/**
 * Measurement and profile importers (spec 42, 43).
 *
 * Accepts:
 *   - JSON hardware profiles (a full profile, or a patch onto a built-in one)
 *   - frequency-response CSV / TXT in the layouts REW, ARTA, Klippel and the
 *     common headphone measurement databases actually produce
 *   - impulse-response WAV
 *
 * SECURITY (spec 99): imported files are data, never code. Nothing is
 * evaluated. Every field is validated and coerced, unknown fields are dropped,
 * numeric ranges are clamped, and array lengths are capped so a hostile file
 * cannot exhaust memory.
 */

import type { AnyProfile, HardwareCategory } from '../types/hardware';
import type { FRCurve, FRPoint } from '../types/curves';
import type { Confidence } from '../types/provenance';
import { CONFIDENCE_ORDER } from '../types/provenance';
import type { StoredMeasurement } from '../database/storage';
import { readWav } from '../render/wav';

const MAX_POINTS = 65536;
const MAX_FILE_BYTES = 64 * 1024 * 1024;

export interface ImportResult {
  profile?: AnyProfile;
  measurement?: StoredMeasurement;
  warnings: string[];
}

export class ImportError extends Error {}

// ---------------------------------------------------------------------------
// Frequency-response text
// ---------------------------------------------------------------------------

/**
 * Parse a frequency-response text file.
 *
 * Handles the layouts that actually turn up: "freq spl phase" from REW, comma
 * or tab or whitespace separated, with or without a header, with '*' or '#' or
 * ';' comment markers.
 */
export function parseFrText(text: string): { curve: FRCurve; warnings: string[] } {
  const warnings: string[] = [];
  const points: FRPoint[] = [];
  const lines = text.split(/\r?\n/);
  let skipped = 0;
  let hasPhase = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    if (line.startsWith('*') || line.startsWith('#') || line.startsWith(';') || line.startsWith('//')) continue;
    const parts = line.split(/[\s,;\t]+/).filter(Boolean);
    if (parts.length < 2) continue;
    const f = Number(parts[0]);
    const db = Number(parts[1]);
    if (!Number.isFinite(f) || !Number.isFinite(db)) {
      skipped++;
      continue;
    }
    if (f <= 0 || f > 1e6) {
      skipped++;
      continue;
    }
    if (parts.length >= 3 && Number.isFinite(Number(parts[2]))) hasPhase = true;
    points.push([f, db]);
    if (points.length >= MAX_POINTS) {
      warnings.push(`Stopped after ${MAX_POINTS} points; the rest of the file was ignored.`);
      break;
    }
  }

  if (points.length < 2) {
    throw new ImportError('No usable frequency/level pairs were found. Expected lines like "20 -3.5" or "20,-3.5".');
  }

  points.sort((a, b) => a[0] - b[0]);
  if (skipped > 0) warnings.push(`${skipped} line(s) were not numeric and were skipped.`);
  if (hasPhase) warnings.push('A third column was present and looks like phase. Phase import is recorded but the DSP currently derives phase from magnitude unless an impulse response is supplied.');

  // Decimate very dense curves onto a log grid: 8,000 raw points would produce
  // an unusably long FIR design pass without adding information.
  let final = points;
  if (points.length > 900) {
    final = decimateLog(points, 480);
    warnings.push(`Curve had ${points.length} points; resampled onto a 480-point logarithmic grid.`);
  }

  return {
    curve: {
      kind: 'fr',
      points: final,
      interp: 'log-pchip',
      confidence: 'MEASURED',
      source: 'Imported measurement file',
    },
    warnings,
  };
}

function decimateLog(points: FRPoint[], n: number): FRPoint[] {
  const fMin = points[0][0];
  const fMax = points[points.length - 1][0];
  const out: FRPoint[] = [];
  let idx = 0;
  for (let i = 0; i < n; i++) {
    const f = fMin * Math.pow(fMax / fMin, i / (n - 1));
    while (idx < points.length - 2 && points[idx + 1][0] < f) idx++;
    const a = points[idx];
    const b = points[Math.min(points.length - 1, idx + 1)];
    const t = b[0] === a[0] ? 0 : (Math.log(f) - Math.log(a[0])) / (Math.log(b[0]) - Math.log(a[0]));
    out.push([f, a[1] + t * (b[1] - a[1])]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Profile JSON
// ---------------------------------------------------------------------------

const CATEGORIES: HardwareCategory[] = ['dac', 'amp', 'speaker', 'headphone', 'cable'];

function coerceString(v: unknown, fallback: string, max = 200): string {
  if (typeof v !== 'string') return fallback;
  const s = v.slice(0, max).replace(/[ -]/g, '');
  return s.trim() || fallback;
}

function coerceNumber(v: unknown, fallback: number, min = -1e9, max = 1e9): number {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
}

/**
 * Validate an imported profile.
 *
 * Structural validation only, deliberately: the schema is large and evolving,
 * so unknown fields are preserved but the fields the engine actually reads are
 * checked and clamped. Anything missing gets a safe default rather than
 * producing a profile that compiles to NaN.
 */
export function validateProfile(raw: unknown): { profile: AnyProfile; warnings: string[] } {
  const warnings: string[] = [];
  if (!raw || typeof raw !== 'object') throw new ImportError('The file is not a JSON object.');
  const o = raw as Record<string, unknown>;

  const category = o.category as HardwareCategory;
  if (!CATEGORIES.includes(category)) {
    throw new ImportError(`"category" must be one of ${CATEGORIES.join(', ')}; got ${JSON.stringify(o.category)}.`);
  }

  const manufacturer = coerceString(o.manufacturer, '');
  const model = coerceString(o.model, '');
  if (!manufacturer || !model) throw new ImportError('Both "manufacturer" and "model" are required.');

  let id = coerceString(o.id, '');
  if (!id) {
    const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    id = `${category === 'headphone' ? 'hp' : category}.${slug(manufacturer)}.${slug(model)}`;
    warnings.push(`No id supplied; generated "${id}".`);
  }

  let confidence = o.confidence as Confidence;
  if (!CONFIDENCE_ORDER.includes(confidence)) {
    confidence = 'UNKNOWN';
    warnings.push('No valid "confidence" field; defaulted to UNKNOWN.');
  }
  if (confidence === 'MEASURED' && !o.measurement) {
    warnings.push('The profile claims MEASURED confidence but carries no measurement block describing the rig and conditions. It has been left as MEASURED, but consider recording where the data came from.');
  }

  const priceRaw = (o.price ?? {}) as Record<string, unknown>;
  const price = {
    amount: coerceNumber(priceRaw.amount, 0, 0, 1e9),
    currency: (['USD', 'EUR', 'GBP', 'JPY'].includes(String(priceRaw.currency)) ? priceRaw.currency : 'USD') as 'USD',
    status: coerceString(priceRaw.status, 'UNKNOWN') as 'UNKNOWN',
    year: priceRaw.year !== undefined ? coerceNumber(priceRaw.year, 0, 1900, 2200) : undefined,
    note: priceRaw.note !== undefined ? coerceString(priceRaw.note, '', 400) : undefined,
  };

  const profile = {
    ...(o as object),
    id,
    category,
    manufacturer,
    model,
    price,
    confidence,
    version: coerceNumber(o.version, 1, 1, 10000),
    revised: coerceString(o.revised, new Date().toISOString().slice(0, 10), 40),
    userDefined: true,
    tags: Array.isArray(o.tags) ? (o.tags as unknown[]).slice(0, 32).map((t) => coerceString(t, '', 40)).filter(Boolean) : ['imported'],
    notes: o.notes !== undefined ? coerceString(o.notes, '', 4000) : undefined,
    provenanceNote: o.provenanceNote !== undefined ? coerceString(o.provenanceNote, '', 4000) : 'Imported profile; no provenance note supplied.',
  } as unknown as AnyProfile;

  // Category-specific minimums, so the compiler never sees an undefined it
  // relies on.
  const required: Record<HardwareCategory, string[]> = {
    dac: ['conversion', 'filters', 'analog', 'nonlinear', 'harmonics', 'noise', 'channels'],
    amp: ['topology', 'form', 'power', 'response', 'output', 'nonlinear', 'harmonics', 'noise', 'channels', 'dynamics', 'clipping'],
    speaker: ['speakerType', 'drivers', 'impedance', 'response', 'crossover', 'cabinet', 'directivity', 'distortion', 'nonlinear'],
    headphone: ['headphoneType', 'fit', 'response', 'distortion', 'nonlinear', 'channels'],
    cable: ['cableType', 'conductor', 'geometry', 'electrical', 'defaultLengthM'],
  };
  const missing = required[category].filter((k) => (profile as unknown as Record<string, unknown>)[k] === undefined);
  if (missing.length) {
    throw new ImportError(
      `A ${category} profile needs these fields: ${missing.join(', ')}. Export a built-in profile from the Measurement Lab to see the expected shape.`,
    );
  }

  return { profile, warnings };
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export async function importProfileJson(file: File): Promise<ImportResult> {
  if (file.size > MAX_FILE_BYTES) {
    throw new ImportError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB; the import limit is ${MAX_FILE_BYTES / 1024 / 1024} MB.`);
  }
  const name = file.name.toLowerCase();

  if (name.endsWith('.wav')) {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const wav = readWav(bytes);
    if (!wav) throw new ImportError('That does not look like a RIFF/WAVE file.');
    const maxLen = 1 << 20;
    const channels = wav.channels.map((c) => Array.from(c.subarray(0, maxLen)));
    return {
      measurement: {
        id: `ir_${Date.now()}`,
        kind: 'impulse',
        name: file.name,
        created: new Date().toISOString(),
        sampleRate: wav.sampleRate,
        channels: wav.channels.length,
        data: channels,
      },
      warnings:
        wav.channels[0].length > maxLen
          ? [`Impulse response truncated to ${maxLen} samples (${(maxLen / wav.sampleRate).toFixed(1)} s).`]
          : [],
    };
  }

  const text = await file.text();

  if (name.endsWith('.json')) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (err) {
      throw new ImportError(`Not valid JSON: ${(err as Error).message}`);
    }
    const { profile, warnings } = validateProfile(parsed);
    return { profile, warnings };
  }

  // CSV / TXT -> frequency response
  const { curve, warnings } = parseFrText(text);
  return {
    measurement: {
      id: `fr_${Date.now()}`,
      kind: 'fr',
      name: file.name,
      created: new Date().toISOString(),
      data: curve,
      meta: { points: curve.points.length },
    },
    warnings,
  };
}

export function exportProfileJson(profile: AnyProfile): string {
  return JSON.stringify(profile, replacer, 2);
}

/** Float32Arrays are not JSON; emit them as plain arrays so they round-trip. */
function replacer(_key: string, value: unknown): unknown {
  if (value instanceof Float32Array || value instanceof Float64Array) return Array.from(value);
  return value;
}

/**
 * Apply an imported frequency-response curve onto an existing profile,
 * producing a new user-owned profile at the next version number.
 */
export function applyMeasuredResponse(base: AnyProfile, curve: FRCurve, note: string): AnyProfile {
  const next = JSON.parse(JSON.stringify(base)) as AnyProfile;
  next.id = `${base.id}.measured`;
  next.version = base.version + 1;
  next.revised = new Date().toISOString().slice(0, 10);
  next.userDefined = true;
  next.confidence = 'MEASURED';
  next.provenanceNote = `${note} Frequency response replaced by imported measurement data; all other fields inherited from ${base.id} v${base.version} and retain their original confidence.`;
  const measured: FRCurve = { ...curve, confidence: 'MEASURED' };
  if (next.category === 'speaker' || next.category === 'headphone') {
    next.response = measured;
  } else if (next.category === 'amp') {
    next.response = measured;
  } else if (next.category === 'dac') {
    next.analog = { ...next.analog, response: measured, confidence: 'MEASURED' };
  }
  return next;
}
